import * as angular from 'angular';
import { isEmpty } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';

import { EasyIdentityResource } from '@mode/legacy/data-access-model-js';
import { Resource } from '@mode/shared/util-halogen';
import { BugsnagHelpers } from '~/lib/bugsnag-helpers';
import { AppStore } from '~/types/main.types';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { getAppName } from '@mode/shared/util-js';

export const IdentityEasy: angular.IComponentOptions = {
  controller: class {
    //
    // Properties
    public easyIdentityCollection: Resource;
    readonly appName = getAppName();
    private query$: BehaviorSubject<string> = new BehaviorSubject(null);
    private easyProviderConfig$: BehaviorSubject<{
      password: EasyIdentityResource;
      google: EasyIdentityResource;
      o365: EasyIdentityResource;
    }> = new BehaviorSubject({
      password: null,
      google: null,
      o365: null,
    });
    public onEasyIdentityUpdate: ($easyIdentityCollection) => void;
    workspaceTitle = 'Workspace';

    //
    // Constructors
    static $inject = ['IdentityEasyService', 'RxStoreService'];

    constructor(
      private IdentityEasyService,
      private rxStoreService: AppStore
    ) {}

    $onChanges(changed: angular.IOnChangesObject) {
      const { easyIdentityCollection } = changed;
      if (!isEmpty(easyIdentityCollection.currentValue)) {
        this.setOptionsWithEasyId(easyIdentityCollection.currentValue);
      }
    }

    //
    // Setting Options for easy provider config
    setOptionsWithEasyId(easyIdentityCollection) {
      this.IdentityEasyService.getEasyIdentityOptions(easyIdentityCollection)
        .then((options) => this.setOptions(options))
        .catch((err) => this.handleError(err));
    }

    setOptions(provierOptions) {
      this.easyProviderConfig$.next(provierOptions);
    }

    //
    // Handlers for updating
    updateAndSetNewOptions(providerId: string) {
      const targetProvider = this.easyProviderConfig$.getValue()[providerId];
      // send a request to update
      this.IdentityEasyService.updateEasyIdentityWithProvider(this.easyIdentityCollection, targetProvider)
        .then((updatedEasyIdentityCollection) => {
          const $easyIdentityCollection = updatedEasyIdentityCollection;
          // tell the parent identity component that there is a new easy-identity resource
          this.onEasyIdentityUpdate({ $easyIdentityCollection });
        })
        .catch((err) => this.handleError(err));
    }

    handleToggleClick(providerId: string) {
      this.updateAndSetNewOptions(providerId);
    }

    handleError(err) {
      BugsnagHelpers.notifySafely(err);
      console.error(err);
    }
  },
  controllerAs: 'ctrl',
  bindings: {
    easyIdentityCollection: '<',
    onEasyIdentityUpdate: '&',
  },
  template: `
  <panel-section class="identity-easy-panel-section">
    <div class="section-header">
      <div class="section-header-description">
        <h4>
          Login Only
        </h4>
        <p>
          Administrators will need to manually add users to {{ ctrl.appName }} if ‘Username &
          password’ is your {{ ctrl.workspaceTitle }}’s only login method.
        </p>
      </div>
    </div>

    <div class="easy-identity-option no-border-top">
      <div class="easy-identity-title">
        <div class="icon-container">
          <span class="icon-person">
          </span>
        </div>
        <h5>
          Username &amp; Password
        </h5>
      </div>

      <identity-provider-toggle
        provider-id="(ctrl.easyProviderConfig$ | async:this).password.id"
        provider-is-default="(ctrl.easyProviderConfig$ | async:this).password.default"
        enabled="(ctrl.easyProviderConfig$ | async:this).password.enabled"
        on-click="ctrl.handleToggleClick($providerId)"
      />
    </div>
  </panel-section>

  <panel-section>
    <div class="section-header">
      <div class="section-header-description">
        <h4>
          SSO
        </h4>
        <p>
          The below login methods automatically create user accounts in {{ ctrl.appName }} for
          emails that match your {{ ctrl.workspaceTitle }}’s claimed domain.
        </p>
      </div>
    </div>

    <div class="easy-identity-option no-border-top">
      <div class="easy-identity-title">
        <div class="icon-container">
          <span class="icon-google-sso">
          </span>
        </div>
        <h5>
          Google OAuth
        </h5>
      </div>
      <identity-provider-toggle
        provider-id="(ctrl.easyProviderConfig$ | async:this).google.id"
        provider-is-default="(ctrl.easyProviderConfig$ | async:this).google.default"
        enabled="(ctrl.easyProviderConfig$ | async:this).google.enabled"
        on-click="ctrl.handleToggleClick($providerId)"
      />
    </div>

    <div class="easy-identity-option">
      <div class="easy-identity-title">
        <div class="icon-container">
          <span class="icon-office-sso">
          </span>
        </div>
        <h5>
          Microsoft Office 365
        </h5>
      </div>
      <identity-provider-toggle
        provider-id="(ctrl.easyProviderConfig$ | async:this).o365.id"
        provider-is-default="(ctrl.easyProviderConfig$ | async:this).o365.default"
        enabled="(ctrl.easyProviderConfig$ | async:this).o365.enabled"
        on-click="ctrl.handleToggleClick($providerId)"
      />
    </div>
  </panel-section>
`,
};
